import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faCoins,
  faImage,
  faPalette,
  faLeaf,
  faKey,
  faCaretDown,
  faListCheck,
  faPrint,
  faBoxesStacked,
} from "@fortawesome/free-solid-svg-icons";

interface AdminItem {
  to: string;
  labelKey: string;
  icon: typeof faChartLine;
}

const ADMIN_ITEMS: AdminItem[] = [
  {
    to: "/admin/statistics",
    labelKey: "headerFooter.adminStatistics",
    icon: faChartLine,
  },
  {
    to: "/admin/price-import",
    labelKey: "headerFooter.adminPriceImport",
    icon: faCoins,
  },
  {
    to: "/admin/logo-import",
    labelKey: "headerFooter.adminLogoImport",
    icon: faImage,
  },
  {
    to: "/admin/design-import",
    labelKey: "headerFooter.adminDesignImport",
    icon: faPalette,
  },
  {
    to: "/admin/co2",
    labelKey: "headerFooter.adminCo2Calculator",
    icon: faLeaf,
  },
  {
    to: "/admin/orders-queue",
    labelKey: "headerFooter.adminOrdersQueue",
    icon: faListCheck,
  },
  {
    to: "/admin/methods",
    labelKey: "headerFooter.adminMethods",
    icon: faPrint,
  },
  {
    to: "/admin/stock-orders",
    labelKey: "headerFooter.adminStockOrders",
    icon: faBoxesStacked,
  },
  {
    to: "/admin/api-keys",
    labelKey: "headerFooter.adminApiKeys",
    icon: faKey,
  },
];

// Deliberately NOT a MUI Menu: the portal-based Menu could survive lazy-route
// navigation as a stuck overlay in the top-left corner of the screen (both the
// item-click close and a close-on-pathname-change effect failed to take effect
// while the destination chunk was loading). A plain in-flow panel positioned
// under the button cannot render anywhere else on the screen, and it unmounts
// with the header - the stuck-overlay failure mode is structurally impossible.
export function AdminMenuDropdown(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const isActive = ADMIN_ITEMS.some((item) =>
    location.pathname.startsWith(item.to),
  );

  return (
    <div ref={rootRef} className="relative h-full">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`group flex items-center gap-2 px-4 h-full text-sm font-medium uppercase tracking-wide transition-colors ${
          isActive || open
            ? "text-[--primary]"
            : "text-[#596675] hover:text-[--primary]"
        }`}
      >
        <span>{t("admin")}</span>
        <FontAwesomeIcon
          icon={faCaretDown}
          className={`text-[10px] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-52 rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {ADMIN_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-slate-50"
              style={{
                textDecoration: "none",
                color: location.pathname.startsWith(item.to)
                  ? "var(--primary)"
                  : "#334155",
              }}
            >
              <FontAwesomeIcon
                icon={item.icon}
                className="w-4 text-slate-400"
              />
              <span>{t(item.labelKey)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
